import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Card, Separator, Text } from '@change/ds-core-ui';
import { BaseSlider } from '@change/ds-forms';
import { CheckIcon, CopyIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { LocalShareAction } from '#src/app/shared/components/localSignerShare';
import { SocialIconWithSuccessState } from '#src/app/shared/components/share';
import { useCarouselScrollFade } from '#src/app/shared/hooks/carousel';

/**
 * EXPERIMENTAL - Constituents-in-PSF "local signer" goal screen.
 *
 * Adapts the control `SharingGoalFlow` + `GoalShareActions`: same `BaseSlider`
 * (1–20, default 5, "20+" cap), the same tiered motivation message, the same
 * "unique share link" card + full channel carousel. The experiment changes the copy
 * (DM-aware, local-signer wording, the 4 Confluence tiers) and adds the blue
 * "I'll help N locals sign" pill above the slider.
 *
 * Presentational: it takes `shareActions` configs as a prop (real on the page,
 * mock in the sandbox) and manages the goal value locally.
 */

const MIN_GOAL = 1;
const MAX_GOAL = 20;
const DEFAULT_GOAL = 5;

// Matches the control goal screen: the copy button shows a check for this long after a successful copy.
const COPIED_RESET_MS = 2500;

const formatGoal = (value: number): string => (value >= MAX_GOAL ? `${value}+` : String(value));

// Breakpoints match the control (1 / 5 / 10 / 20); copy + emoji are the experiment's.
const TIERS: ReadonlyArray<{ min: number; emoji: string; key: string }> = [
	{ min: 1, emoji: '🌱', key: 'tier_1' },
	{ min: 5, emoji: '⭐️', key: 'tier_5' },
	{ min: 10, emoji: '💪', key: 'tier_10' },
	{ min: 20, emoji: '🚀', key: 'tier_20' },
];

const tierFor = (goal: number): (typeof TIERS)[number] => {
	let result = TIERS[0];
	for (const tier of TIERS) if (goal >= tier.min) result = tier;
	return result;
};

const visibleUrl = (url: string): string => {
	try {
		const parsed = new URL(url);
		return `${parsed.host}${parsed.pathname}`;
	} catch {
		return url;
	}
};

const Greeting: React.FC<Readonly<{ userName?: string; target: string }>> = ({ userName, target }) => (
	<div className="flex flex-col gap-3 text-center">
		<Text as="p" variant="body" size="small" fontWeight="bold" className="text-typography-weak m-0">
			{userName ? (
				<Translate value="corgi.local_signer_goal.greeting" replacements={{ name: userName }} />
			) : (
				<Translate value="corgi.local_signer_goal.greeting_no_name" />
			)}
		</Text>
		<Text as="h1" variant="heading" size="small" className="text-typography-strong m-0">
			<Translate value="corgi.local_signer_goal.heading" />
		</Text>
		<Text as="p" variant="body" size="large" className="text-typography-strong">
			<Translate value="corgi.local_signer_goal.body" replacements={{ target }} />
		</Text>
	</div>
);

const CommitmentSlider: React.FC<Readonly<{ goal: number; onChange: (value: number) => void }>> = ({
	goal,
	onChange,
}) => {
	const { translate, translatePlural } = useI18n();
	const tier = tierFor(goal);
	return (
		<div className="flex flex-col gap-3">
			<span className="bg-fill-information-strong inline-flex w-fit rounded-full px-4 py-2">
				<Text as="span" variant="body" size="small" fontWeight="bold" className="text-typography-inverse-strong">
					{translatePlural('corgi.local_signer_goal.pill', goal, { count: goal })}
				</Text>
			</span>

			<div className="flex flex-col gap-2 px-1">
				<BaseSlider
					ariaLabel={translate('corgi.local_signer_goal.slider_aria')}
					value={goal}
					minValue={MIN_GOAL}
					maxValue={MAX_GOAL}
					step={1}
					onChange={onChange}
					size="thick"
					formatValue={formatGoal}
					data-qa="local-signer-goal-slider"
				/>
				<Text as="p" variant="body" size="small" className="text-typography-weak">
					<span aria-hidden>{tier.emoji}</span> <Translate value={`corgi.local_signer_goal.${tier.key}`} />
				</Text>
			</div>
		</div>
	);
};

/** Unique share link card (reuses the control GoalShareActions layout). */
const ShareLinkCard: React.FC<{ copy: LocalShareAction }> = ({ copy }) => {
	const { translate } = useI18n();
	const [copied, setCopied] = useState(false);
	const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Swap the copy icon for a check on a successful copy, reverting after a beat (mirrors the control
	// goal screen's GoalShareActions).
	const handleAfterCopy = useCallback(() => {
		setCopied(true);
		if (resetTimerRef.current != null) clearTimeout(resetTimerRef.current);
		resetTimerRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
	}, []);

	useEffect(
		() => () => {
			if (resetTimerRef.current != null) clearTimeout(resetTimerRef.current);
		},
		[],
	);

	return (
		<Card elevation className="bg-background-base flex items-center justify-between gap-3 rounded-lg p-4 pl-5">
			<div className="min-w-0 flex-1">
				<Text as="div" fontWeight="bold" className="text-typography-strong text-xs leading-5">
					<Translate value="corgi.local_signer_goal.share_link_label" />
				</Text>
				<div className="text-typography-weak overflow-hidden text-xs leading-5 text-ellipsis whitespace-nowrap">
					{visibleUrl(copy.url)}
				</div>
			</div>
			<copy.ShareAction
				onAfterAction={(success) => {
					if (success) handleAfterCopy();
				}}
			>
				<Button
					iconOnly
					variant="primary"
					size="x-small"
					aria-label={translate('corgi.local_signer_goal.copy_aria')}
					leadingIcon={
						copied ? (
							<CheckIcon
								data-qa="copied-icon-check"
								className="text-icon-success dark:text-icon-strong-fixed h-5! w-5!"
							/>
						) : (
							<CopyIcon className="h-5! w-5!" />
						)
					}
					className="h-11 w-11 p-0"
				/>
			</copy.ShareAction>
		</Card>
	);
};

/**
 * Horizontally-scrollable channel row, matching the control `GoalShareActions` carousel: it shows the
 * full available channel set (not a fixed subset), bleeds to the card's right edge so the next channel
 * peeks (signalling it scrolls), and fades the right edge while there is more to scroll. `onScroll` lets
 * the page fire `psf_share_channel_carousel_scroll`.
 */
const ChannelCarousel: React.FC<Readonly<{ actions: readonly LocalShareAction[]; onScroll?: () => void }>> = ({
	actions,
	onScroll,
}) => {
	const { ref, showFade } = useCarouselScrollFade(actions.length);
	return (
		<div
			ref={ref}
			onScroll={onScroll}
			className={cn(
				'-mr-6 flex snap-x snap-proximity [scrollbar-width:none] overflow-x-auto overscroll-contain py-1 [&::-webkit-scrollbar]:hidden',
				showFade &&
					'[mask-image:linear-gradient(to_right,black_calc(100%_-_72px),transparent)] [-webkit-mask-image:linear-gradient(to_right,black_calc(100%_-_72px),transparent)]',
			)}
		>
			{actions.map((action) => (
				<div key={action.channel} className="w-20 max-w-20 shrink-0 snap-start">
					<SocialIconWithSuccessState
						className="w-20 max-w-20 text-center"
						buttonConfig={{
							ShareAction: action.ShareAction,
							icon: action.icon,
							label: action.label,
							successLabel: action.successLabel,
							variant: action.variant,
						}}
					/>
				</div>
			))}
		</div>
	);
};

type Props = {
	/** The petition's relevant location (city or state, by reach), shown as "[location] leaders". */
	location?: string;
	userName?: string;
	/** Share configs (copy + social channels). Real on the page, mock in the sandbox. */
	shareActions: readonly LocalShareAction[];
	/** Fired once when the signer first scrolls the channel carousel (`psf_share_channel_carousel_scroll`). */
	onCarouselScroll?: () => void;
	/** Fired whenever the goal slider value changes (`psf_share_goal_slider_change`). */
	onGoalChange?: (value: number) => void;
};

export const LocalSignerGoalComponent: React.FC<Props> = ({
	location,
	userName,
	shareActions,
	onCarouselScroll,
	onGoalChange,
}) => {
	const { translate } = useI18n();
	const target = location
		? translate('corgi.local_signer_goal.location_leaders', { location })
		: translate('corgi.local_signer_goal.decision_makers');
	const [goal, setGoal] = useState(DEFAULT_GOAL);

	const handleGoalChange = useCallback(
		(value: number) => {
			setGoal(value);
			onGoalChange?.(value);
		},
		[onGoalChange],
	);

	const copy = shareActions.find((a) => a.channel === 'copy');
	const socials = shareActions.filter((a) => a.channel !== 'copy');

	return (
		<div
			className="bg-background-base relative flex min-h-[calc(100svh-23rem)] w-full flex-col overflow-hidden rounded-2xl"
			data-qa="local-signer-goal"
		>
			{/* Brand tint that fades down into the card's base colour by the share-link box (Figma 203:7767).
			    A vertical transparent->base layer painted over the DS `gradient-background-light-2` tint, which has its own dark-mode value (no mask / no z-index tricks,
			    so it renders reliably across browsers); content sits on top as normal background content. */}
			<div
				className="flex flex-col gap-6 px-6 pt-10 pb-6"
				style={{
					backgroundImage:
						'linear-gradient(to bottom, transparent 0%, transparent 35%, var(--color-background-base) 100%), var(--gradient-background-light-2)',
				}}
			>
				<Greeting userName={userName} target={target} />
				<CommitmentSlider goal={goal} onChange={handleGoalChange} />
				{copy && <ShareLinkCard copy={copy} />}
			</div>
			<div className="flex flex-col gap-6 px-6 pt-6">
				<ChannelCarousel actions={socials} onScroll={onCarouselScroll} />
				<Separator className="m-0" />
			</div>
		</div>
	);
};
